import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Folder, Star, Copyright, Menu, X, Heart, Code, Sun, Moon, Monitor } from 'lucide-react';

// FontAwesome 아이콘들을 HTML 클래스 방식으로 사용
const FontAwesomeIcon = ({ iconClass, size = 20 }) => (
  <i className={iconClass} style={{ fontSize: `${size}px` }}></i>
);

const Navigation = ({ navigation, isMobile, personal, colorTheme, currentTheme, systemDarkMode, toggleColorTheme, onDownloadClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showFooterText, setShowFooterText] = useState(false);
  const [angelWingsSpread, setAngelWingsSpread] = useState(false);
  const [showResumeSubmenu, setShowResumeSubmenu] = useState(false);
  const [submenuHeight, setSubmenuHeight] = useState(0);
  const submenuRef = useRef(null);
  const currentYear = new Date().getFullYear();

  // Resume 페이지에 있을 때 자동으로 submenu 열기
  useEffect(() => {
    if (location.pathname === '/resume') {
      setShowResumeSubmenu(true);
    }
  }, [location.pathname]);

  // Resume submenu 높이 계산
  useEffect(() => {
    const updateHeight = () => {
      if (submenuRef.current) {
        setSubmenuHeight(submenuRef.current.scrollHeight);
      }
    };

    updateHeight();
    // DOM 렌더링 완료 후 재측정
    setTimeout(updateHeight, 10);
  }, [showResumeSubmenu, navigation]);

  // Color Theme Toggler Icon Component - 와이어프레임 기반 (교정된 햇살)
  const ThemeToggleIcon = ({ theme, size = 20 }) => {
    if (theme === 'auto') {
      // 브라우저 테마를 따르는 상태는 운영체제 테마와 상관없이 늘 천사(날개 편) — 햇빛(해)·달빛(달)과 늘 구분되게.
      // 전에는 운영체제가 라이트면 해를 그려 햇빛 상태와 같아졌다(우분투 라이트 테마에서 천사가 안 보임, CLAUDE.md Phase 21)
      return (
        <div style={{ transform: 'scale(0.15)', transformOrigin: 'center' }}>
          <AngelIcon size={size} wingsSpread={true} />
        </div>
      );
    } else if (theme === 'light') {
      // Light mode - 해 (단일 색상 통일)
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Sun rays - 9시, 3시 방향 간격 조정 */}
          <g stroke="#FF8C00" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="2" x2="12" y2="4" />
            <line x1="12" y1="20" x2="12" y2="22" />
            <line x1="22" y1="12" x2="19" y2="12" />  {/* 3시 방향 간격 조정 */}
            <line x1="5" y1="12" x2="2" y2="12" />   {/* 9시 방향 간격 조정 */}
            <line x1="18.36" y1="5.64" x2="17.07" y2="6.93" />
            <line x1="6.93" y1="17.07" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="18.36" x2="17.07" y2="17.07" />
            <line x1="6.93" y1="6.93" x2="5.64" y2="5.64" />
          </g>
          {/* Large sun center - 단일 색상 통일 */}
          <circle cx="12" cy="12" r="5" fill="#FF8C00" stroke="#FF8C00" strokeWidth="1" opacity="0.8" />
          <circle cx="12" cy="12" r="3" fill="#FF8C00" stroke="#FF8C00" strokeWidth="1.5" />
        </svg>
      );
    } else {
      // Dark theme - 달 (그림자 윤곽선) - 원래대로 복원
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" 
                fill="#FFD700" stroke="#8B7355" strokeWidth="1.5" opacity="0.9" />
          <circle cx="15" cy="9" r="0.8" fill="#FFF8DC" opacity="0.6" />
          <circle cx="17" cy="12" r="0.5" fill="#FFF8DC" opacity="0.4" />
        </svg>
      );
    }
  };

  // Angel Icon Component - 형제요소들과 비슷한 크기로 확대
  // 라이트 테마(밝은 배경)에서는 윤곽·후광을 진하게 — 연한 황갈색 #D2B48C·금색은 밝은 주황 배경과 대비가 1.2~1.9:1 이라 거의 안 보인다.
  // 다크 테마는 원래 색 그대로 (CLAUDE.md Phase 21)
  const AngelIcon = ({ size = 20, wingsSpread = false }) => {
    const onLight = currentTheme !== 'dark';
    const line = onLight ? '#8B5A2B' : '#D2B48C';
    const halo = onLight ? '#A16207' : 'gold';
    const haloInner = onLight ? '#CA8A04' : '#FFD700';
    return (
      <svg width={size * 6.5} height={size * 6.5} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Halo - 금색 링 (더 큰 크기) */}
        <ellipse cx="50" cy="20" rx="15" ry="4" stroke={halo} strokeWidth="4" fill="none" opacity="1" />
        <ellipse cx="50" cy="20" rx="10" ry="2.5" stroke={haloInner} strokeWidth="2.5" fill="none" opacity="0.8" />
      
        {/* Head - 큰 원형 머리 (진한 베이지 윤곽선) */}
        <circle cx="50" cy="32" r="10" fill="transparent" stroke={line} strokeWidth="4" opacity="0.9" />
      
        {/* Eyes - 심플한 점 (더 크게) */}
        <circle cx="45" cy="30" r="2.5" fill={line} />
        <circle cx="55" cy="30" r="2.5" fill={line} />
      
        {/* Simple smile (더 크게) */}
        <path d="M43 36 Q50 40 57 36" stroke={line} strokeWidth="3.5" fill="none" strokeLinecap="round" />
      
        {/* Body - 삼각형 치마 (허리가 기준점) - 더 큰 크기 */}
        <path d="M50 47 L35 80 L65 80 Z" fill="transparent" stroke={line} strokeWidth="4.5" opacity="0.9" />
      
        {/* Arms - 간단한 선 (더 큰 크기) */}
        <line x1="35" y1="53" x2="27" y2="61" stroke={line} strokeWidth="4" strokeLinecap="round" />
        <line x1="65" y1="53" x2="73" y2="61" stroke={line} strokeWidth="4" strokeLinecap="round" />
      
        {/* Legs - 새로 추가 (더 크게) */}
        <line x1="43" y1="80" x2="43" y2="90" stroke={line} strokeWidth="4.5" strokeLinecap="round" />
        <line x1="57" y1="80" x2="57" y2="90" stroke={line} strokeWidth="4.5" strokeLinecap="round" />
      
        {/* Wings - 허리(47px) 기준으로 10시-2시 방향으로 펼쳐짐 */}
        <g>
          {/* Left Wing - 10시 방향 펼침 / 뚜렷한 접힘 */}
          <path 
            d={wingsSpread 
              ? "M40 47 Q20 30 12 38 Q15 55 33 52 Q42 47 40 47" 
              : "M42 47 Q37 42 40 52 Q42 58 45 52 Q47 47 42 47"
            }
            fill="white" 
            stroke={line}
            strokeWidth="3.5"
            opacity="0.95"
            className="transition-all duration-600 ease-in-out"
          />
        
          {/* Left Wing feathers - 뾰족한 끝 (접힌 상태도 뚜렷하게) */}
          <path 
            d={wingsSpread 
              ? "M15 32 L19 36 L23 32 L27 36 L31 32 L35 36 L39 32" 
              : "M40 44 L42 47 L45 44 L42 50 L40 47"
            }
            stroke={line}
            strokeWidth="2.5"
            fill="none"
            opacity="0.8"
            className="transition-all duration-600 ease-in-out"
          />
          <path 
            d={wingsSpread 
              ? "M14 40 L18 44 L22 40 L26 44 L30 40 L34 44 L38 40" 
              : "M41 50 L42 53 L44 50 L45 53 L46 50"
            }
            stroke={line}
            strokeWidth="2.5"
            fill="none"
            opacity="0.8"
            className="transition-all duration-600 ease-in-out"
          />
        
          {/* Right Wing - 2시 방향 펼침 / 뚜렷한 접힘 */}
          <path 
            d={wingsSpread 
              ? "M60 47 Q80 30 88 38 Q85 55 67 52 Q58 47 60 47" 
              : "M58 47 Q63 42 60 52 Q58 58 55 52 Q53 47 58 47"
            }
            fill="white" 
            stroke={line}
            strokeWidth="3.5"
            opacity="0.95"
            className="transition-all duration-600 ease-in-out"
          />
        
          {/* Right Wing feathers - 뾰족한 끝 (접힌 상태도 뚜렷하게) */}
          <path 
            d={wingsSpread 
              ? "M85 32 L81 36 L77 32 L73 36 L69 32 L65 36 L61 32" 
              : "M60 44 L58 47 L55 44 L58 50 L60 47"
            }
            stroke={line}
            strokeWidth="2.5"
            fill="none"
            opacity="0.8"
            className="transition-all duration-600 ease-in-out"
          />
          <path 
            d={wingsSpread 
              ? "M86 40 L82 44 L78 40 L74 44 L70 40 L66 44 L62 40" 
              : "M59 50 L58 53 L56 50 L55 53 L54 50"
            }
            stroke={line}
            strokeWidth="2.5"
            fill="none"
            opacity="0.8"
            className="transition-all duration-600 ease-in-out"
          />
        </g>
      
        {/* Simple decorations when wings spread */}
        {wingsSpread && (
          <>
            {/* Wing tip sparkles */}
            <circle cx="15" cy="38" r="2" fill="white" opacity="0.9" className="animate-pulse" />
            <circle cx="85" cy="38" r="2" fill="white" opacity="0.9" className="animate-pulse" />
            <circle cx="22" cy="47" r="1.5" fill={haloInner} opacity="0.7" className="animate-pulse" />
            <circle cx="78" cy="47" r="1.5" fill={haloInner} opacity="0.7" className="animate-pulse" />
          </>
        )}
      
        {/* Feet (더 크게) */}
        <ellipse cx="41" cy="93" rx="2.5" ry="1.5" fill="transparent" stroke={line} strokeWidth="3" />
        <ellipse cx="59" cy="93" rx="2.5" ry="1.5" fill="transparent" stroke={line} strokeWidth="3" />
      </svg>
    );
  };

  const getIcon = (iconName, size = 28) => {
    const icons = {
      Home: Home,
      AddressCardRegular: ({ size }) => <FontAwesomeIcon iconClass="fa-regular fa-address-card" size={size} />, // Resume용 FontAwesome
      Folder: Folder,
      PenToSquareRegular: ({ size }) => <FontAwesomeIcon iconClass="fa-regular fa-pen-to-square" size={size} />, // Posts용 FontAwesome
      Star: Star,
      CommentsRegular: ({ size }) => <FontAwesomeIcon iconClass="fa-regular fa-comments" size={size} />, // Contacts용 FontAwesome
      ChevronDown: Copyright, // Footer icon changed to Copyright
      Angel: ({ size }) => <AngelIcon size={size} wingsSpread={angelWingsSpread} />,
      ThemeToggle: ({ size }) => <ThemeToggleIcon theme={colorTheme} size={size} />
    };
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent size={size} /> : null;
  };

  // Create mobile navigation with Angel in the middle (7 items only)
  const mobileNavigation = isMobile 
    ? [
        ...navigation.slice(0, 3), // Home, Resume, Projects (subItems 포함)
        { id: "angel", label: "Angel", href: "#angel", icon: "Angel" }, // Angel in middle
        ...navigation.slice(3, -1) // Posts, Wishes, Contacts (excluding Footer)
      ]
    : navigation.slice(0, -1);

  // Footer text appears after panel animation completes
  useEffect(() => {
    if (isMenuOpen) {
      // Wait for panel sliding animation to complete (500ms), then show text
      const timer = setTimeout(() => {
        setShowFooterText(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShowFooterText(false);
    }
  }, [isMenuOpen]);

  const handleNavClick = (href, id, external = false) => {
    if (id === 'angel') {
      // Angel button clicked - toggle wings (no auto-reset)
      setAngelWingsSpread(!angelWingsSpread);
      return;
    }

    if (id === 'theme-toggle') {
      // Theme toggle button clicked
      toggleColorTheme();
      return;
    }

    if (id === 'resume') {
      // Resume button clicked - toggle submenu only
      setShowResumeSubmenu(!showResumeSubmenu);
      return;
    }

    if (id === 'download' || id === 'resume_download') {
      // Download button clicked - trigger download modal
      if (onDownloadClick) {
        onDownloadClick();
      }
      setShowResumeSubmenu(false);
      setIsMenuOpen(false);
      if (!onDownloadClick) {
        // 홈·Contacts 에는 다운로드 창이 없다 → Resume 페이지로 가면서 창을 열어 달라는 표시를 넘긴다 (CLAUDE.md Phase 24)
        navigate('/resume', { state: { openDownloadModal: true } });
      }
      return;
    }

    if (href.startsWith('#') || href.includes('#')) {
      // Special handling for #edu to enable toggle
      if (href.endsWith('#edu')) {
        if (window.toggleEduHighlight) {
          window.toggleEduHighlight();
        }
        // Navigate if needed
        if (!location.pathname.includes('/resume')) {
          navigate(href);
        } else if (window.location.hash !== '#edu') {
          window.location.hash = '#edu';
        }
      } else if (href.startsWith('#')) {
        window.location.hash = href;
      } else {
        // href includes '#' but doesn't start with it (e.g., /resume#why_me)
        navigate(href, { replace: false });

        // navigate 후 강제로 스크롤 실행 (React Router가 hash를 감지 못할 경우 대비)
        setTimeout(() => {
          const hash = href.split('#')[1];
          if (hash) {
            const element = document.getElementById(hash);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }, 200);
      }
      // Resume submenu items를 클릭해도 submenu 유지 (유지!)
      // setShowResumeSubmenu는 변경하지 않음
    } else if (external) {
      // Open external links in new window
      window.open(href, '_blank', 'noopener,noreferrer');
      setShowResumeSubmenu(false);
    } else {
      // Route navigation
      navigate(href);
      setShowResumeSubmenu(false);
    }
    setIsMenuOpen(false);
  };

  if (isMobile) {
    return (
      <>
        {/* Floating Theme Toggle - Mobile - 상우 대칭 여백으로 더 가깝게, 스크롤바 위에 표시 */}
        <button
          onClick={toggleColorTheme}
          className="fixed top-2 z-[9999] w-12 h-12 flex items-center justify-center transition-all duration-300"
          style={{right: '8px'}}
          title={`현재: ${colorTheme === 'auto' ? '브라우저 테마' : colorTheme === 'light' ? '햇빛 테마' : '달빛 테마'}`}
        >
          <ThemeToggleIcon theme={colorTheme} size={28} />
        </button>

        {/* Footer Bar - Fixed above navigation - 테마별 색상, 스크롤바 위에 표시 */}
        <div className={`fixed left-0 right-0 backdrop-blur-md z-[9999] ${
          currentTheme === 'dark'
            ? 'bg-gray-800/90 text-gray-100 border-gray-600/30'
            : 'bg-orange-800/90 text-white border-orange-200/30'
        }`} style={{bottom: '40px'}}>
          <div className="text-center px-4" style={{paddingTop: '3px', paddingBottom: '3px'}}>
            <span className={`text-xs ${
              currentTheme === 'dark' ? 'text-gray-200' : 'text-orange-100'
            }`} style={{lineHeight: '12px', display: 'block'}}>
              © {currentYear} {personal.name}. All rights reserved
            </span>
          </div>
        </div>

        {/* Backdrop */}
        {isMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-[90]"
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Sliding Menu Panel - Mobile - 테마별 색상, 스크롤바 위에 표시 */}
        <div className={`fixed bottom-8 left-0 right-0 backdrop-blur-md border-t shadow-2xl z-[9999] transition-transform duration-300 ease-out ${
          isMenuOpen ? 'translate-y-0' : 'translate-y-full'
        } ${
          currentTheme === 'dark' 
            ? 'bg-gray-800/95 border-gray-600' 
            : 'bg-orange-50/95 border-orange-200'
        }`}>
          <div className="py-4">
            {/* All navigation items including Angel in sliding menu */}
            {mobileNavigation.map((item) => (
              <div key={item.id}>
                <button
                  onClick={() => handleNavClick(item.href, item.id, item.external)}
                  className={`w-full flex items-center px-6 py-4 text-left transition-colors ${
                    item.id === 'angel' 
                      ? 'text-pink-700 hover:bg-pink-50 hover:text-pink-900' 
                      : 'text-orange-700 hover:bg-orange-50 hover:text-orange-900'
                  }`}
                >
                  <div className={`mr-4 flex items-center justify-center relative ${
                    item.id === 'angel' 
                      ? 'text-pink-600' 
                      : currentTheme === 'dark'
                      ? 'text-orange-400'  // 달님 테마 포인트 색상
                      : 'text-orange-600'
                  }`} style={{
                    width: '80px', 
                    height: '80px', 
                    padding: '11.4px',
                    overflow: item.id === 'angel' ? 'visible' : 'hidden'
                  }}>
                    {getIcon(item.icon, item.id === 'angel' ? 45 : 57.1)}
                  </div>
                  <span className="font-medium">{item.label}</span>
                </button>
                
                {/* 모바일 Resume 하위 메뉴 */}
                {item.id === 'resume' && showResumeSubmenu && (
                  <div className="ml-20 space-y-2 pb-2">
                    {/* 원본 navigation에서 resume 찾아서 subItems 사용 */}
                    {navigation.find(navItem => navItem.id === 'resume')?.subItems?.map((subItem) => (
                      <button
                        key={subItem.id}
                        onClick={() => handleNavClick(subItem.href, subItem.id)}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          currentTheme === 'dark' 
                            ? 'text-orange-300 hover:bg-gray-700/30 hover:text-orange-200' 
                            : 'text-orange-500 hover:bg-orange-50/50 hover:text-orange-600'
                        }`}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      <>
        {/* Resume 하위 메뉴 슬라이딩 패널 - Mobile Only - 항상 DOM에 존재 */}
        {isMobile && (
          <>
            {/* Backdrop */}
            <div
              className={`fixed inset-0 bg-black/50 z-[95] transition-opacity duration-500 ${
                showResumeSubmenu ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              onClick={() => setShowResumeSubmenu(false)}
            />

            {/* 아래에서 위로 슬라이딩되는 Resume 메뉴 패널 */}
            <div
              ref={submenuRef}
              className={`fixed left-0 right-0 backdrop-blur-md shadow-2xl z-[100] transition-transform duration-500 ease-in-out ${
                showResumeSubmenu ? 'translate-y-0' : 'translate-y-full'
              } ${
                currentTheme === 'dark'
                  ? 'bg-gray-800/95'
                  : 'bg-orange-50/95'
              }`}
              style={{
                height: submenuHeight > 0 ? `${submenuHeight}px` : 'auto',
                bottom: '40px'
              }}
            >
              <div className="m-0 p-0" style={{ paddingBottom: '1.5rem' }}>
                {/* Resume 하위 메뉴들 - 폰트 높이만 남기고 여백 전부 제거 */}
                <div className="m-0 p-0">
                  {navigation.find(navItem => navItem.id === 'resume')?.subItems?.map((subItem) => (
                    <button
                      key={subItem.id}
                      onClick={() => handleNavClick(subItem.href, subItem.id)}
                      className={`w-full text-left m-0 p-0 transition-colors ${
                        currentTheme === 'dark'
                          ? 'text-orange-300 hover:bg-gray-700/30 hover:text-orange-200 bg-gray-700/20'
                          : 'text-orange-600 hover:bg-orange-100/50 hover:text-orange-700 bg-orange-100/30'
                      }`}
                      style={{
                        height: '3rem',
                        lineHeight: '3rem',
                        padding: '0',
                        margin: '0'
                      }}
                    >
                      <span className="font-medium" style={{
                        lineHeight: '3rem',
                        height: '3rem',
                        display: 'block',
                        paddingLeft: '0.5rem',
                        margin: '0'
                      }}>
                        {subItem.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </>

        {/* Bottom Navigation Bar - Mobile - 테마별 색상, 스크롤바 위에 표시 */}
        <div className={`fixed bottom-0 left-0 right-0 backdrop-blur-md border-t shadow-lg z-[9999] ${
          currentTheme === 'dark' 
            ? 'bg-gray-800/95 border-gray-600' 
            : 'bg-orange-50/95 border-orange-200'
        }`}>
          <div className="flex items-center justify-around py-1">
            {/* All navigation items including Angel in middle */}
            {mobileNavigation.map((item) => (
              <div key={item.id} className="flex flex-col items-center relative">
                <button
                  onClick={() => handleNavClick(item.href, item.id, item.external)}
                  className={`flex flex-col items-center p-0.5 transition-colors ${
                    item.id === 'angel' 
                      ? 'text-pink-500 hover:text-pink-700 relative z-10' 
                      : currentTheme === 'dark'
                      ? 'text-orange-400 hover:text-orange-300'  // 달님 테마 포인트 색상
                      : 'text-orange-600 hover:text-orange-800'
                  }`}
                >
                  <div className="flex items-center justify-center relative" style={{
                    width: '28px', 
                    height: '28px', 
                    padding: '4.2px',
                    overflow: item.id === 'angel' ? 'visible' : 'hidden'
                  }}>
                    {getIcon(item.icon, item.id === 'angel' ? 20 : 19.6)}
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  // Desktop/Tablet Navigation
  return (
    <>
      {/* Backdrop */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Resume Submenu Backdrop - Desktop */}
      {!isMobile && showResumeSubmenu && (
        <div
          className="fixed inset-0 bg-black/30 z-[60]"
          onClick={() => setShowResumeSubmenu(false)}
        />
      )}

      {/* Sliding Menu Panel - Desktop - 나무 기둥 색상 테마 */}
      <div
        className={`fixed top-0 right-0 h-full backdrop-blur-md shadow-lg z-50 transition-all duration-500 ease-in-out ${
          currentTheme === 'dark'
            ? 'bg-gray-800/90'
            : 'bg-orange-100/80'  // 햇빛 테마 배경색
        }`}
        style={{
          // 접힌 폭은 index.css 의 --desktop-nav-width (각 페이지 본문의 오른쪽 inset 과 같은 값)
          width: isMenuOpen ? 'calc(100vw / 7)' : 'var(--desktop-nav-width)'
        }}
      >
        
        {/* Color Theme Toggle Button - 항상 우측 상단 고정 (하늘의 해/달), 원형 호버 */}
        <button
          onClick={toggleColorTheme}
          className={`absolute top-2 right-2 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 z-10 ${
            currentTheme === 'dark' 
              ? 'hover:bg-gray-700/50' 
              : 'hover:bg-orange-100/50'
          }`}
          title={`현재: ${colorTheme === 'auto' ? '브라우저' : colorTheme === 'light' ? '햇빛' : '달빛'} 테마`}
        >
          <div className="flex items-center justify-center" style={{width: '32px', height: '32px', padding: '4px'}}>
            <ThemeToggleIcon theme={colorTheme} size={24} />
          </div>
        </button>

        {/* Menu Items */}
        {isMenuOpen && (
          <div className="absolute top-20 left-0 right-0 bottom-16 flex flex-col">
            
            {/* Navigation Items (exclude footer) - 여백 최소화로 텍스트 공간 확보 */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              {navigation.slice(0, -1).map((item) => (
                <div key={item.id}>
                  <button
                    onClick={() => handleNavClick(item.href, item.id, item.external)}
                    className={`w-full flex items-center text-left transition-colors px-1 py-1 hover:rounded ${
                      currentTheme === 'dark' 
                        ? 'text-orange-400 hover:bg-gray-700/50 hover:text-orange-300' 
                        : 'text-orange-600 hover:bg-orange-100/50 hover:text-orange-700'  // 따뜻한 오렌지
                    }`}
                    style={{height: '60px'}}
                  >
                    <div className="flex items-center justify-center flex-shrink-0 mr-1" style={{width: '32px', height: '32px', padding: '4px'}}>
                      {getIcon(item.icon, 24)}
                    </div>
                    <span className="text-sm font-medium truncate">{item.label}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Content - 절대 위치로 하단 고정, 나무 기둥 색상 */}
        {isMenuOpen && (
          <div className={`absolute bottom-0 left-0 right-0 py-3 text-center h-16 flex items-center justify-center ${
            currentTheme === 'dark' 
              ? 'bg-gradient-to-r from-gray-900 to-gray-800 text-gray-200' 
              : 'bg-gradient-to-r from-orange-700 to-orange-600 text-orange-100'  // 햇빛 테마 색상
          }`}>
            {showFooterText && (
              <span className={`text-sm ${
                currentTheme === 'dark' ? 'text-gray-300' : 'text-orange-100'
              }`}>
                © {currentYear} {personal.name} All rights reserved
              </span>
            )}
          </div>
        )}

        {/* Collapsed Menu Icons */}
        {!isMenuOpen && (
          <div className="absolute top-20 left-0 right-0 bottom-0 flex flex-col">
            {/* Navigation Icons (exclude footer) */}
            <div className="flex-1 py-4 space-y-3">
              {navigation.slice(0, -1).map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.href, item.id, item.external)}
                  className={`w-full flex justify-center items-center transition-colors h-16 py-2 hover:rounded ${
                    currentTheme === 'dark' 
                      ? 'text-orange-400 hover:text-orange-300 hover:bg-gray-700/50' 
                      : 'text-orange-600 hover:text-orange-700 hover:bg-orange-100/50'  // 따뜻한 오렌지
                  }`}
                  title={item.label}
                >
                  <div className="flex items-center justify-center" style={{width: '32px', height: '32px', padding: '4px'}}>
                    {getIcon(item.icon, 24)}
                  </div>
                </button>
              ))}
            </div>
            
            {/* Footer Copyright Icon - 클릭하여 확장, 완전히 하단에 고정 */}
            <button 
              className={`flex justify-center items-center py-3 transition-all duration-300 ease-in-out h-16 hover:rounded ${
                currentTheme === 'dark' 
                  ? 'hover:bg-gray-700/50' 
                  : 'hover:bg-orange-100/50'  // 햇빛 테마
              }`}
              onClick={() => setIsMenuOpen(true)}
              title="Footer - 클릭하여 확장"
            >
              <div className={`transition-colors duration-200 flex items-center justify-center ${
                currentTheme === 'dark' 
                  ? 'text-orange-400 hover:text-orange-300' 
                  : 'text-orange-600 hover:text-orange-700'  // 따뜻한 오렌지
              }`} style={{width: '32px', height: '32px', padding: '4px'}}>
                <Copyright size={24} />
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Resume Submenu Sliding Panel - Desktop Only - 우측에서 좌측으로 슬라이딩 */}
      {!isMobile && (
        <div
          className={`fixed top-0 h-full backdrop-blur-md shadow-2xl transition-all duration-[2000ms] ease-in-out ${
            currentTheme === 'dark'
              ? 'bg-gray-800/95'
              : 'bg-orange-50/95'
          }`}
          style={{
            right: showResumeSubmenu ? (isMenuOpen ? 'calc(100vw / 7)' : 'var(--desktop-nav-width)') : '0',
            width: 'calc(100vw / 7)',
            zIndex: 9998,
            opacity: showResumeSubmenu ? 1 : 0,
            pointerEvents: showResumeSubmenu ? 'auto' : 'none'
          }}
        >
          <div className="h-full flex flex-col">
            {/* Resume 하위 메뉴 */}
            <div className="flex-1 pt-20 px-4">
              <h3 className={`text-lg font-bold mb-4 ${
                currentTheme === 'dark' ? 'text-orange-300' : 'text-orange-700'
              }`}>
                Resume
              </h3>
              <div className="space-y-2">
                {navigation.find(navItem => navItem.id === 'resume')?.subItems?.map((subItem) => (
                  <button
                    key={subItem.id}
                    onClick={() => handleNavClick(subItem.href, `resume_${subItem.id}`)}
                    className={`w-full text-left px-4 py-3 text-sm transition-colors rounded ${
                      currentTheme === 'dark'
                        ? 'text-orange-300 hover:bg-gray-700/50 hover:text-orange-200'
                        : 'text-orange-600 hover:bg-orange-100/50 hover:text-orange-700'
                    }`}
                  >
                    {subItem.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;